import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  FlatList,
  KeyboardAvoidingView,
  Platform,
  ActivityIndicator,
} from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { format } from 'date-fns';
import Icon from 'react-native-vector-icons/Ionicons';

import twilioChat, { Conversation, Message } from '@/services/twilioChat';
import { colors, font, space, radius } from '@/theme/chalkCourt';

type ConversationParams = { twilioSid: string; title?: string };
type ConversationRoute = RouteProp<{ Conversation: ConversationParams }, 'Conversation'>;

const isSameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const ConversationScreen: React.FC = () => {
  const route = useRoute<ConversationRoute>();
  const navigation = useNavigation<any>();
  const { twilioSid, title } = route.params;

  // Source of truth for "is this my message" is the Twilio Client identity
  // (used to mint the access token), not the Redux username — the backend
  // mints the token with the JWT subject which may differ in case from the
  // stored USERPROFILE.USERNAME.
  const [myIdentity, setMyIdentity] = useState<string | null>(null);
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasOlder, setHasOlder] = useState(true);
  const listRef = useRef<FlatList<any>>(null);
  const activeRef = useRef(true);
  const messagePaginatorRef = useRef<any>(null);
  // Track whether the user is pinned near the bottom; only then do we
  // auto-scroll on new messages, so reading old history isn't interrupted.
  const nearBottomRef = useRef(true);

  useEffect(() => {
    navigation.setOptions({ title: title ?? 'Conversation' });
  }, [navigation, title]);

  useEffect(() => {
    activeRef.current = true;
    let conv: Conversation | null = null;
    // Re-check activeRef inside the handler — if the screen has unmounted
    // between the Twilio push and the React update, drop the update on the
    // floor instead of touching unmounted state.
    const onAdded = (m: Message) => {
      if (!activeRef.current) {
        return;
      }
      setMessages((prev) => [...prev, m]);
      conv?.setAllMessagesRead().catch(() => undefined);
    };

    const start = async () => {
      try {
        const [identity, c] = await Promise.all([
          twilioChat.getIdentity(),
          twilioChat.getConversation(twilioSid),
        ]);
        conv = c;
        const page = await c.getMessages(50);
        messagePaginatorRef.current = page;
        const history = [...page.items];
        if (!activeRef.current) {
          return;
        }
        setMyIdentity(identity);
        setConversation(c);
        setMessages(history);
        setHasOlder(Boolean((page as any).hasPrevPage));
        setLoading(false);
        c.on('messageAdded', onAdded);
        c.setAllMessagesRead().catch(() => undefined);
      } catch (e: any) {
        if (activeRef.current) {
          setError(e?.message ?? 'Could not open conversation');
          setLoading(false);
        }
      }
    };
    start();
    return () => {
      activeRef.current = false;
      conv?.off('messageAdded', onAdded);
    };
  }, [twilioSid]);

  // Auto-scroll only when the user was already pinned to the bottom —
  // otherwise a new message would yank them away from history they're reading.
  useEffect(() => {
    if (messages.length > 0 && nearBottomRef.current) {
      setTimeout(() => listRef.current?.scrollToEnd({ animated: true }), 50);
    }
  }, [messages.length]);

  const loadOlder = async () => {
    if (loadingOlder || !hasOlder || !messagePaginatorRef.current) {
      return;
    }
    setLoadingOlder(true);
    try {
      const prev = await messagePaginatorRef.current.prevPage();
      if (!activeRef.current) {
        return;
      }
      messagePaginatorRef.current = prev;
      const older = [...prev.items];
      setMessages((current) => [...older, ...current]);
      setHasOlder(Boolean(prev.hasPrevPage));
    } catch (e) {
      // Non-fatal; just stop trying so we don't hammer.
      setHasOlder(false);
    } finally {
      if (activeRef.current) {
        setLoadingOlder(false);
      }
    }
  };

  const handleScroll = (e: any) => {
    // Treat "within 80px of bottom" as still-at-bottom for the purpose of
    // auto-scrolling on new messages.
    const { layoutMeasurement, contentOffset, contentSize } = e.nativeEvent;
    const distanceFromBottom =
      contentSize.height - layoutMeasurement.height - contentOffset.y;
    nearBottomRef.current = distanceFromBottom < 80;
  };

  const send = async () => {
    if (!conversation || !text.trim() || sending) {
      return;
    }
    setSending(true);
    try {
      await twilioChat.sendMessage(conversation, text);
      setText('');
    } catch (e: any) {
      setError(e?.message ?? 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const grouped = useMemo(() => {
    // Insert day-separator pseudo-items between messages on different days.
    const out: Array<
      { kind: 'day'; key: string; date: Date } | { kind: 'msg'; key: string; msg: Message }
    > = [];
    let lastDate: Date | null = null;
    for (const m of messages) {
      const ts = (m as any).dateCreated as Date | undefined;
      if (ts && (!lastDate || !isSameDay(lastDate, ts))) {
        out.push({ kind: 'day', key: `d-${ts.toISOString()}`, date: ts });
        lastDate = ts;
      }
      out.push({ kind: 'msg', key: `m-${(m as any).sid ?? Math.random()}`, msg: m });
    }
    return out;
  }, [messages]);

  if (loading) {
    return (
      <View style={styles.centered}>
        <ActivityIndicator color={colors.accent} />
        <Text style={styles.loadingText}>Connecting…</Text>
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.centered}>
        <Icon name="alert-circle" size={32} color={colors.warn} />
        <Text style={styles.errorText}>{error}</Text>
      </View>
    );
  }

  return (
    <KeyboardAvoidingView
      style={styles.screen}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      keyboardVerticalOffset={Platform.OS === 'ios' ? 90 : 0}>
      <FlatList
        ref={listRef}
        data={grouped}
        keyExtractor={(item) => item.key}
        contentContainerStyle={styles.list}
        onScroll={handleScroll}
        scrollEventThrottle={120}
        onEndReachedThreshold={0.05}
        ListHeaderComponent={
          hasOlder ? (
            <TouchableOpacity
              accessibilityLabel="Load older messages"
              style={styles.loadOlder}
              activeOpacity={0.7}
              disabled={loadingOlder}
              onPress={loadOlder}>
              {loadingOlder ? (
                <ActivityIndicator color={colors.accent} />
              ) : (
                <Text style={styles.loadOlderText}>Load earlier messages</Text>
              )}
            </TouchableOpacity>
          ) : null
        }
        renderItem={({ item }) => {
          if (item.kind === 'day') {
            return (
              <View style={styles.daySeparator}>
                <Text style={styles.dayText}>{format(item.date, 'EEEE, MMM d')}</Text>
              </View>
            );
          }
          const m = item.msg as any;
          const mine = m.author === myIdentity;
          const time = m.dateCreated ? format(m.dateCreated as Date, 'h:mm a') : '';
          return (
            <View style={[styles.bubbleRow, mine ? styles.bubbleRowMine : styles.bubbleRowTheirs]}>
              <View style={[styles.bubble, mine ? styles.bubbleMine : styles.bubbleTheirs]}>
                {!mine && <Text style={styles.author}>{m.author}</Text>}
                <Text style={[styles.body, mine && styles.bodyMine]}>{m.body ?? ''}</Text>
                <Text style={[styles.time, mine && styles.timeMine]}>{time}</Text>
              </View>
            </View>
          );
        }}
      />
      <View style={styles.composer}>
        <TextInput
          style={styles.input}
          value={text}
          onChangeText={setText}
          placeholder="Write a message…"
          placeholderTextColor={colors.inkFaint}
          multiline
        />
        <TouchableOpacity
          style={[styles.sendBtn, (!text.trim() || sending) && styles.sendBtnDisabled]}
          activeOpacity={0.85}
          disabled={!text.trim() || sending}
          onPress={send}>
          <Icon name="send" size={20} color={colors.onAccent} />
        </TouchableOpacity>
      </View>
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: colors.bg },
  centered: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.bg,
    padding: space.lg,
  },
  loadingText: {
    fontFamily: font.bodyMed,
    fontSize: 14,
    color: colors.inkSoft,
    marginTop: space.sm,
  },
  errorText: {
    fontFamily: font.bodyMed,
    fontSize: 14,
    color: colors.inkSoft,
    marginTop: space.sm,
    textAlign: 'center',
  },

  list: { padding: space.md },

  loadOlder: {
    alignSelf: 'center',
    paddingVertical: space.sm,
    paddingHorizontal: space.md,
    marginBottom: space.sm,
  },
  loadOlderText: {
    fontFamily: font.bodyBold,
    fontSize: 11,
    letterSpacing: 1,
    textTransform: 'uppercase',
    color: colors.accentDark,
  },

  daySeparator: {
    alignItems: 'center',
    marginVertical: space.md,
  },
  dayText: {
    fontFamily: font.bodyBold,
    fontSize: 11,
    letterSpacing: 1.5,
    textTransform: 'uppercase',
    color: colors.inkFaint,
  },

  bubbleRow: {
    flexDirection: 'row',
    marginBottom: space.sm,
  },
  bubbleRowMine: { justifyContent: 'flex-end' },
  bubbleRowTheirs: { justifyContent: 'flex-start' },
  bubble: {
    maxWidth: '78%',
    borderRadius: radius.md,
    paddingVertical: space.sm,
    paddingHorizontal: space.md,
  },
  bubbleMine: {
    backgroundColor: colors.accent,
    borderBottomRightRadius: 4,
  },
  bubbleTheirs: {
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.line,
    borderBottomLeftRadius: 4,
  },
  author: {
    fontFamily: font.bodyBold,
    fontSize: 11,
    letterSpacing: 0.5,
    color: colors.accentDark,
    marginBottom: 2,
  },
  body: {
    fontFamily: font.body,
    fontSize: 15,
    lineHeight: 20,
    color: colors.ink,
  },
  bodyMine: { color: colors.onAccent },
  time: {
    fontFamily: font.bodyMed,
    fontSize: 10.5,
    color: colors.inkFaint,
    marginTop: 4,
    alignSelf: 'flex-end',
  },
  timeMine: { color: 'rgba(255,255,255,0.75)' },

  composer: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    padding: space.sm,
    borderTopWidth: 1,
    borderTopColor: colors.line,
    backgroundColor: colors.bg,
  },
  input: {
    flex: 1,
    minHeight: 40,
    maxHeight: 120,
    backgroundColor: colors.surface,
    borderRadius: radius.md,
    borderWidth: 1,
    borderColor: colors.line,
    paddingHorizontal: space.md,
    paddingVertical: space.sm,
    fontFamily: font.body,
    fontSize: 15,
    color: colors.ink,
    marginRight: space.sm,
  },
  sendBtn: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: colors.accent,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sendBtnDisabled: { opacity: 0.4 },
});

export default ConversationScreen;
